<script>
  import { onMount } from "svelte";
  import { authFetch } from "../authService";
  import { getApiUrl } from "../api";
  import { t } from "../i18nStore";

  let tasks = [];
  let availableModels = [];
  let selectedTask = null;
  let selectedModelToAdd = "";
  let isLoading = true;
  let isSaving = false;
  let errorMsg = "";
  let successMsg = "";

  async function loadData() {
    try {
      isLoading = true;
      errorMsg = "";
      successMsg = "";

      // 1. Fetch AI task configs
      const taskRes = await authFetch(getApiUrl("/admin/ai-task-configs"));
      if (taskRes.ok) {
        const taskData = await taskRes.json();
        tasks = taskData.tasks || [];
        if (tasks.length > 0 && !selectedTask) {
          selectedTask = JSON.parse(JSON.stringify(tasks[0]));
        } else if (selectedTask) {
          const found = tasks.find(t => t.id === selectedTask.id);
          if (found) {
            selectedTask = JSON.parse(JSON.stringify(found));
          }
        }
      } else {
        errorMsg = $t('admin.ai_tasks.save_error');
      }

      // 2. Fetch AI models (source of available models)
      const modelRes = await authFetch(getApiUrl("/admin/ai-models"));
      if (modelRes.ok) {
        const modelData = await modelRes.json();
        // Lookup rule: only enabled models (isEnabled = true)
        availableModels = (modelData.models || []).filter(m => m.isEnabled);
      }
    } catch (e) {
      errorMsg = "Error loading data: " + e.message;
    } finally {
      isLoading = false;
    }
  }

  function selectTask(task) {
    selectedTask = JSON.parse(JSON.stringify(task));
    errorMsg = "";
    successMsg = "";
    selectedModelToAdd = "";
  }

  function getEligibleModelsToAdd() {
    if (!selectedTask) return [];
    const currentModelIds = new Set((selectedTask.modelPipeline || []).map(p => p.modelId));
    return availableModels.filter(m => !currentModelIds.has(m.id));
  }

  function addModelToPipeline() {
    if (!selectedModelToAdd || !selectedTask) return;
    const model = availableModels.find(m => m.id === selectedModelToAdd);
    if (!model) return;

    if (!selectedTask.modelPipeline) {
      selectedTask.modelPipeline = [];
    }

    // Default priority calculated based on current count
    const nextPriority = (selectedTask.modelPipeline.length === 0) ? 100 : Math.max(10, 100 - selectedTask.modelPipeline.length * 10);
    selectedTask.modelPipeline = [
      ...selectedTask.modelPipeline,
      {
        modelId: model.id,
        provider: model.provider,
        priority: nextPriority
      }
    ];

    selectedModelToAdd = "";
  }

  function removeModelFromPipeline(index) {
    if (!selectedTask || !selectedTask.modelPipeline) return;
    selectedTask.modelPipeline = selectedTask.modelPipeline.filter((_, i) => i !== index);
  }

  function moveModel(index, direction) {
    if (!selectedTask || !selectedTask.modelPipeline) return;
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= selectedTask.modelPipeline.length) return;

    const list = [...selectedTask.modelPipeline];
    const temp = list[index];
    list[index] = list[targetIdx];
    list[targetIdx] = temp;

    // Recalculate descending priorities based on new order
    list.forEach((item, idx) => {
      item.priority = 100 - idx * 10;
    });

    selectedTask.modelPipeline = list;
  }

  async function saveConfig() {
    if (!selectedTask) return;
    try {
      isSaving = true;
      errorMsg = "";
      successMsg = "";

      if (selectedTask.timeoutSeconds <= 0) {
        selectedTask.timeoutSeconds = 8;
      }

      const res = await authFetch(getApiUrl(`/admin/ai-task-configs/${selectedTask.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          displayName: selectedTask.displayName,
          description: selectedTask.description,
          timeoutSeconds: parseInt(selectedTask.timeoutSeconds, 10),
          modelPipeline: selectedTask.modelPipeline || []
        })
      });

      if (res.ok) {
        successMsg = $t('admin.ai_tasks.save_success');
        await loadData();
      } else {
        const data = await res.json().catch(() => ({}));
        errorMsg = data.error || $t('admin.ai_tasks.save_error');
      }
    } catch (e) {
      errorMsg = "Error saving task config: " + e.message;
    } finally {
      isSaving = false;
    }
  }

  function getLocalizedTaskName(taskId, defaultName) {
    const key = `admin.ai_tasks.task_${taskId}`;
    const translated = $t(key);
    return (translated && translated !== key) ? translated : defaultName;
  }

  onMount(async () => {
    await loadData();
  });
</script>

<div class="task-configs-container">
  {#if errorMsg}
    <div class="alert alert-danger">{errorMsg}</div>
  {/if}
  {#if successMsg}
    <div class="alert alert-success">{successMsg}</div>
  {/if}

  {#if isLoading}
    <div class="loading">{$t('common.loading')}</div>
  {:else}
    <p class="subtitle">{$t('admin.ai_tasks.subtitle')}</p>

    <!-- Task Selector Tabs -->
    <div class="task-tabs">
      {#each tasks as task}
        <button
          class="task-tab-btn {selectedTask && selectedTask.id === task.id ? 'active' : ''}"
          on:click={() => selectTask(task)}
        >
          {getLocalizedTaskName(task.id, task.displayName || task.id)}
        </button>
      {/each}
    </div>

    {#if selectedTask}
      <div class="card config-card">
        <div class="card-header">
          <h3>{getLocalizedTaskName(selectedTask.id, selectedTask.displayName)} ({selectedTask.id})</h3>
          <span class="badge badge-info">{selectedTask.modelPipeline ? selectedTask.modelPipeline.length : 0} Models</span>
        </div>

        <div class="card-body">
          <p class="task-desc">{selectedTask.description}</p>

          <!-- Timeout Setting -->
          <div class="form-group timeout-group">
            <label for="timeout-input"><strong>{$t('admin.ai_tasks.timeout')}:</strong></label>
            <div class="timeout-control">
              <input
                id="timeout-input"
                type="number"
                min="1"
                max="120"
                bind:value={selectedTask.timeoutSeconds}
                class="form-control timeout-input"
              />
              <span class="unit-label">s</span>
            </div>
          </div>

          <!-- Model Pipeline -->
          <div class="pipeline-section">
            <h4>{$t('admin.ai_tasks.model_pipeline')}</h4>
            <p class="hint">{$t('admin.ai_tasks.pipeline_hint')}</p>

            {#if selectedTask.id === 'image_scanning'}
              <div class="alert alert-info" style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem; font-size: 0.875rem;">
                <span>{$t('admin.ai_tasks.fast_multimodal_hint')}</span>
                <button
                  type="button"
                  class="btn-secondary"
                  style="padding: 0.3rem 0.75rem; font-size: 0.8rem; border-radius: 4px; cursor: pointer;"
                  on:click={() => {
                    if (confirm($t('admin.ai_tasks.seed_fast_models_confirm'))) {
                      selectedTask.timeoutSeconds = 20;
                      selectedTask.modelPipeline = [
                        { modelId: 'gemini-2.5-flash', provider: 'gemini', priority: 100 },
                        { modelId: 'gemini-3-flash-preview', provider: 'gemini', priority: 90 },
                        { modelId: 'gemini-3.5-flash', provider: 'gemini', priority: 80 },
                        { modelId: 'gemini-3.6-flash', provider: 'gemini', priority: 70 },
                        { modelId: 'gemini-3.5-flash-lite', provider: 'gemini', priority: 60 }
                      ];
                    }
                  }}
                >
                  {$t('admin.ai_tasks.seed_fast_models')}
                </button>
              </div>
            {/if}

            {#if !selectedTask.modelPipeline || selectedTask.modelPipeline.length === 0}
              <div class="alert alert-warning">{$t('admin.ai_tasks.empty_pipeline')}</div>
            {:else}
              <table class="table pipeline-table">
                <thead>
                  <tr>
                    <th style="width: 50px;">#</th>
                    <th>{$t('admin.ai_models.table_name')}</th>
                    <th>{$t('admin.ai_models.table_provider')}</th>
                    <th>{$t('admin.ai_models.table_priority')}</th>
                    <th>{$t('admin.ai_models.table_actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {#each selectedTask.modelPipeline as item, idx}
                    <tr>
                      <td class="order-cell">{idx + 1}</td>
                      <td class="model-id-cell"><strong>{item.modelId}</strong></td>
                      <td>
                        <span class="badge badge-{item.provider}">{item.provider}</span>
                      </td>
                      <td>
                        <input
                          type="number"
                          bind:value={item.priority}
                          class="priority-input"
                          min="0"
                        />
                      </td>
                      <td class="action-cell">
                        <button
                          class="btn btn-xs btn-outline"
                          disabled={idx === 0}
                          on:click={() => moveModel(idx, -1)}
                          title={$t('admin.ai_tasks.move_up')}
                        >
                          ▲ {$t('admin.ai_tasks.move_up')}
                        </button>
                        <button
                          class="btn btn-xs btn-outline"
                          disabled={idx === selectedTask.modelPipeline.length - 1}
                          on:click={() => moveModel(idx, 1)}
                          title={$t('admin.ai_tasks.move_down')}
                        >
                          ▼ {$t('admin.ai_tasks.move_down')}
                        </button>
                        <button
                          class="btn btn-xs btn-danger"
                          on:click={() => removeModelFromPipeline(idx)}
                          title={$t('admin.ai_tasks.remove')}
                        >
                          ✕ {$t('admin.ai_tasks.remove')}
                        </button>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            {/if}

            <!-- Add Model to Pipeline -->
            <div class="add-model-panel">
              <h5>{$t('admin.ai_tasks.add_model')}</h5>
              {#if getEligibleModelsToAdd().length === 0}
                <p class="text-muted">{$t('admin.ai_tasks.no_enabled_models')}</p>
              {:else}
                <div class="add-controls">
                  <select bind:value={selectedModelToAdd} class="form-control model-select">
                    <option value="">{$t('admin.ai_tasks.select_model')}</option>
                    {#each getEligibleModelsToAdd() as m}
                      <option value={m.id}>
                        [{m.provider}] {m.id} - {m.displayName || m.id}
                      </option>
                    {/each}
                  </select>
                  <button
                    class="btn btn-secondary"
                    disabled={!selectedModelToAdd}
                    on:click={addModelToPipeline}
                  >
                    + {$t('admin.ai_tasks.add_model')}
                  </button>
                </div>
              {/if}
            </div>
          </div>

          <!-- Save Button -->
          <div class="save-bar">
            <button class="btn btn-primary btn-lg" on:click={saveConfig} disabled={isSaving}>
              {#if isSaving}
                {$t('admin.ai_tasks.saving')}
              {:else}
                💾 {$t('admin.ai_tasks.save')}
              {/if}
            </button>
          </div>
        </div>
      </div>
    {/if}
  {/if}
</div>

<style>
  .task-configs-container {
    padding: 0.5rem 0;
  }

  .subtitle {
    color: #6c757d;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }

  .task-tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 0.25rem;
  }

  .task-tab-btn {
    background: none;
    border: none;
    padding: 0.6rem 1.2rem;
    font-size: 1rem;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
    border-radius: 6px 6px 0 0;
    transition: all 0.2s ease;
  }

  .task-tab-btn:hover {
    color: #007bff;
    background-color: #f8f9fa;
  }

  .task-tab-btn.active {
    color: #007bff;
    border-bottom: 3px solid #007bff;
    font-weight: 600;
    background-color: #e7f1ff;
  }

  .card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  }

  .card-header {
    padding: 1rem 1.25rem;
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .card-header h3 {
    margin: 0;
    font-size: 1.2rem;
    color: #212529;
  }

  .card-body {
    padding: 1.25rem;
  }

  .task-desc {
    color: #495057;
    margin-bottom: 1.25rem;
  }

  .timeout-group {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 0.75rem 1rem;
    background-color: #f8f9fa;
    border-radius: 6px;
    width: fit-content;
  }

  .timeout-control {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  .timeout-input {
    width: 80px;
    padding: 0.35rem 0.5rem;
    font-size: 1rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    text-align: center;
  }

  .unit-label {
    color: #6c757d;
    font-weight: 500;
  }

  .pipeline-section {
    margin-top: 1.5rem;
  }

  .pipeline-section h4 {
    margin-bottom: 0.25rem;
    font-size: 1.1rem;
  }

  .hint {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 1rem;
  }

  .pipeline-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1.5rem;
  }

  .pipeline-table th,
  .pipeline-table td {
    padding: 0.65rem 0.75rem;
    border-bottom: 1px solid #dee2e6;
    text-align: left;
    vertical-align: middle;
  }

  .pipeline-table th {
    background-color: #f8f9fa;
    font-weight: 600;
    font-size: 0.85rem;
    color: #495057;
  }

  .order-cell {
    font-weight: bold;
    color: #6c757d;
  }

  .priority-input {
    width: 70px;
    padding: 0.25rem 0.4rem;
    font-size: 0.9rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    text-align: center;
  }

  .action-cell {
    display: flex;
    gap: 0.35rem;
  }

  .btn-xs {
    padding: 0.2rem 0.45rem;
    font-size: 0.75rem;
    border-radius: 3px;
  }

  .btn-outline {
    background: #fff;
    border: 1px solid #ced4da;
    color: #495057;
    cursor: pointer;
  }

  .btn-outline:hover:not(:disabled) {
    background: #e9ecef;
  }

  .btn-outline:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .add-model-panel {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 6px;
    border: 1px dashed #ced4da;
    margin-bottom: 1.5rem;
  }

  .add-model-panel h5 {
    margin: 0 0 0.75rem 0;
    font-size: 0.95rem;
    color: #495057;
  }

  .add-controls {
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .model-select {
    flex: 1;
    max-width: 450px;
    padding: 0.45rem 0.75rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .save-bar {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
  }

  .badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .badge-gemini {
    background-color: #e8f0fe;
    color: #1a73e8;
  }

  .badge-deepseek {
    background-color: #e6f4ea;
    color: #137333;
  }

  .badge-info {
    background-color: #e1f5fe;
    color: #0288d1;
  }

  .alert {
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    border-radius: 4px;
  }

  .alert-danger {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  .alert-success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .alert-warning {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeeba;
  }

  .btn-primary {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 0.5rem 1.25rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #0056b3;
  }

  .btn-secondary {
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 0.45rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #5a6268;
  }

  .btn-danger {
    background-color: #dc3545;
    color: white;
    border: none;
    cursor: pointer;
  }

  .btn-danger:hover {
    background-color: #bd2130;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .loading {
    text-align: center;
    padding: 2rem;
    color: #6c757d;
  }
</style>
